/**
 * Who can see a file, why, and what stops them.
 *
 * Read access is decided by shouldFileBeVisible in utility.js, which is an OR
 * over five independent grants:
 *
 *   1  file.uploadedBy is the user
 *   2  file.managers holds the user
 *   3  the user's role is System Digital Manager, which sees everything
 *   4  user.files holds the file key
 *   5  a group in file.groups holds the user
 *
 * Grant 4 is the awkward one. An individual grant is written to
 * users/<key>/files/<fileKey> and nowhere else, so a file carries no record of
 * the people added to it one by one. The only way to answer "who was added
 * individually" is to walk every user, which is why buildAccessIndex turns that
 * into a reverse index once rather than per file.
 *
 * Everything here is pure and takes the shapes DatabaseContext publishes, which
 * are not consistent between nodes:
 *
 *   file.managers    array of user key strings
 *   file.groups      array of group key strings
 *   file.ancestors   array of folder key strings, includes the file's own folder
 *   folder.ancestors array of folder key strings, excludes the folder itself
 *   folder.users     array of user key strings, only meaningful on hubs
 *   group.users      array of { key } objects
 *   user.files       array of file key strings
 *
 * Nothing in this module writes, and nothing in it plans a write.
 */

// The role that sees every file without any per file record
export const GLOBAL_ROLE = "System Digital Manager";

// folder.parent for a folder that sits at the top of the tree
export const ROOT_PARENT = "Root";

/**
 * Everything the per file questions need, built once.
 *
 * grantsByFile is the reverse of user.files. Without it, every file costs a
 * full pass over users, which is the difference between a sweep that finishes
 * and one that doesn't.
 */
export function buildAccessIndex(users, groups, folders) {
    let usersByKey = new Map();
    let grantsByFile = new Map();
    let globalRoleKeys = new Set();

    users.forEach((user) => {
        usersByKey.set(user.key, user);

        if (user.role === GLOBAL_ROLE) {
            globalRoleKeys.add(user.key);
        }

        (user.files || []).forEach((fileKey) => {
            let holders = grantsByFile.get(fileKey);

            if (!holders) {
                holders = new Set();
                grantsByFile.set(fileKey, holders);
            }

            holders.add(user.key);
        });
    });

    let groupsByKey = new Map(
        groups.map((group) => [
            group.key,
            {
                key: group.key,
                name: group.name,
                hub: group.hub,
                members: new Set((group.users || []).map((u) => u.key)),
            },
        ]),
    );

    let foldersByKey = new Map(
        (folders || []).map((folder) => [folder.key, folder]),
    );

    return {
        usersByKey: usersByKey,
        groupsByKey: groupsByKey,
        foldersByKey: foldersByKey,
        grantsByFile: grantsByFile,
        globalRoleKeys: globalRoleKeys,
    };
}

/**
 * Every reason this user can see this file, not just the first one that fires.
 * "In the Under 18s group and added individually" is the answer that settles a
 * support question; "because of a group" is the answer that starts another one.
 *
 * visible must agree with shouldFileBeVisible for the same inputs. There is a
 * test holding the two together, because a tool that explains a rule the app no
 * longer follows is worse than no tool.
 */
export function explainFileVisibility(file, user, index) {
    let reasons = [];

    if (file.uploadedBy && file.uploadedBy === user.key) {
        reasons.push({ type: "uploader" });
    }

    if ((file.managers || []).includes(user.key)) {
        reasons.push({ type: "manager" });
    }

    if (user.role === GLOBAL_ROLE) {
        reasons.push({ type: "role", role: user.role });
    }

    if ((user.files || []).includes(file.key)) {
        reasons.push({ type: "individual" });
    }

    (file.groups || []).forEach((groupKey) => {
        let group = index.groupsByKey.get(groupKey);

        if (group && group.members.has(user.key)) {
            reasons.push({
                type: "group",
                groupKey: group.key,
                groupName: group.name,
                hubKey: group.hub,
            });
        }
    });

    return { visible: reasons.length > 0, reasons: reasons };
}

// A reason a person can read, for the output log
export function describeReason(reason) {
    switch (reason.type) {
        case "uploader":
            return "uploaded the file";
        case "manager":
            return "file manager";
        case "role":
            return reason.role;
        case "individual":
            return "added individually";
        case "group":
            return `group ${reason.groupName || "(no name)"} [${reason.groupKey}]`;
        default:
            return reason.type;
    }
}

// Whether every reason a user has is the global role. Those users are counted
// rather than listed: they can see every file in the database, so naming them
// against one file says nothing and buries the grants that were actually made.
function onlyByRole(reasons) {
    return reasons.every((reason) => reason.type === "role");
}

/**
 * Who can see this file, and why. Reached through the index rather than a pass
 * over users, so the cost is the number of grants on the file and not the size
 * of the platform.
 *
 * Users whose only claim is the global role are counted in globalRoleOnly. A
 * System Digital Manager who is also, say, in one of the file's groups is
 * listed like anyone else, with the role shown alongside the grant that would
 * still hold if the role were taken away.
 */
export function fileViewers(file, index) {
    let reasonsByUser = new Map();

    const add = (userKey, reason) => {
        // A key on a file may name a user who no longer exists. That grant
        // gives nobody access, so it is not a viewer; deadGrants reports it.
        if (!index.usersByKey.has(userKey)) {
            return;
        }

        let existing = reasonsByUser.get(userKey);

        if (!existing) {
            existing = [];
            reasonsByUser.set(userKey, existing);
        }

        existing.push(reason);
    };

    if (file.uploadedBy) {
        add(file.uploadedBy, { type: "uploader" });
    }

    (file.managers || []).forEach((userKey) =>
        add(userKey, { type: "manager" }),
    );

    (index.grantsByFile.get(file.key) || new Set()).forEach((userKey) =>
        add(userKey, { type: "individual" }),
    );

    (file.groups || []).forEach((groupKey) => {
        let group = index.groupsByKey.get(groupKey);

        if (!group) {
            return;
        }

        group.members.forEach((userKey) =>
            add(userKey, {
                type: "group",
                groupKey: group.key,
                groupName: group.name,
                hubKey: group.hub,
            }),
        );
    });

    // The role is added last so it reads as an extra note against a real grant
    index.globalRoleKeys.forEach((userKey) => {
        if (index.usersByKey.has(userKey)) {
            add(userKey, { type: "role", role: GLOBAL_ROLE });
        }
    });

    let viewers = [];
    let globalRoleOnly = 0;

    reasonsByUser.forEach((reasons, userKey) => {
        if (onlyByRole(reasons)) {
            globalRoleOnly += 1;
            return;
        }

        viewers.push({ user: index.usersByKey.get(userKey), reasons: reasons });
    });

    viewers.sort((a, b) =>
        (a.user.fullName || "").localeCompare(b.user.fullName || ""),
    );

    return { viewers: viewers, globalRoleOnly: globalRoleOnly };
}

/**
 * The other direction: every file this user can see, and why. A System Digital
 * Manager sees the whole database, so their list is the file table and tells
 * you nothing; roleOnly says so instead of printing it.
 */
export function filesVisibleToUser(user, files, index) {
    if (user.role === GLOBAL_ROLE) {
        return { roleOnly: true, files: [] };
    }

    let visible = [];

    files.forEach((file) => {
        let explained = explainFileVisibility(file, user, index);

        if (explained.visible) {
            visible.push({ file: file, reasons: explained.reasons });
        }
    });

    return { roleOnly: false, files: visible };
}

/**
 * The folder chain from the file's folder back to the top, root first.
 *
 * broken is set when the walk runs out of folders before reaching Root, which
 * means the finder's nesting loop drops the folder and everything under it: the
 * file has an owner it cannot be reached through.
 */
export function folderChain(folderKey, index) {
    let chain = [];
    let seen = new Set();
    let current = index.foldersByKey.get(folderKey);
    let broken = false;

    while (current) {
        if (seen.has(current.key)) {
            // A parent loop would spin here forever. It also makes the folder
            // unreachable, so it is reported the same way as a broken chain.
            broken = true;
            break;
        }

        seen.add(current.key);
        chain.unshift(current);

        if (!current.parent || current.parent === ROOT_PARENT) {
            break;
        }

        let parent = index.foldersByKey.get(current.parent);

        if (!parent) {
            broken = true;
            break;
        }

        current = parent;
    }

    return { chain: chain, broken: broken };
}

// The path a person would read, deepest folder last. Kept here rather than
// reusing utility.filePath because that one needs the folder to exist and this
// has to describe files whose folder does not.
export function filePathString(file, index) {
    let folder = index.foldersByKey.get(file.folder);

    if (!folder) {
        return `(folder ${file.folder || "unset"} is missing)/${file.displayName || file.filename || file.key}`;
    }

    let walked = folderChain(file.folder, index);
    let names = walked.chain.map((f) => f.name || "(no name)");

    return (
        (walked.broken ? ".../" : "") +
        names.join("/") +
        "/" +
        (file.displayName || file.filename || file.key)
    );
}

/**
 * Reachability, which is a separate question from permission.
 *
 * FileNavigatorPage builds a user's folder tree from the hubs they belong to
 * plus a walk over file.ancestors for every file they can see. A user who is
 * not a folder manager and not in the hub only gets a subfolder in their tree
 * because the file's own ancestors name it. So a file whose ancestors have
 * fallen behind its folder is granted and still invisible, and that is entirely
 * silent in the app.
 *
 * file.ancestors should be the folder's ancestors plus the folder itself.
 */
export function fileReachability(file, index) {
    let issues = [];
    let folder = index.foldersByKey.get(file.folder);

    if (!folder) {
        issues.push({
            code: "MISSING_FOLDER",
            severity: "critical",
            detail: `file.folder is ${file.folder || "unset"}, which is not a folder`,
        });

        return issues;
    }

    let walked = folderChain(file.folder, index);

    if (walked.broken) {
        issues.push({
            code: "BROKEN_CHAIN",
            severity: "critical",
            detail: `the folder chain above ${folder.name || folder.key} does not reach the top of the tree`,
        });
    }

    let expected = new Set([...(folder.ancestors || []), folder.key]);
    let actual = new Set(file.ancestors || []);

    let missing = [...expected].filter((key) => !actual.has(key));
    let extra = [...actual].filter((key) => !expected.has(key));

    if (missing.length > 0 || extra.length > 0) {
        issues.push({
            code: "ANCESTORS_MISMATCH",
            severity: "warning",
            detail:
                `file.ancestors does not match its folder` +
                (missing.length > 0 ? `, missing ${missing.join(", ")}` : "") +
                (extra.length > 0 ? `, unexpected ${extra.join(", ")}` : ""),
            missing: missing,
            extra: extra,
        });
    }

    return issues;
}

/**
 * Grants on the file that give nobody anything, and grants that work but look
 * like a mistake. None of this stops a file being seen by the people it
 * reached; it explains files that reach fewer people than someone expected.
 */
export function fileGrantIssues(file, index) {
    let issues = [];

    (file.groups || []).forEach((groupKey) => {
        let group = index.groupsByKey.get(groupKey);

        if (!group) {
            issues.push({
                code: "DEAD_GROUP",
                severity: "warning",
                detail: `group ${groupKey} no longer exists, so the grant does nothing`,
            });

            return;
        }

        let realMembers = [...group.members].filter((userKey) =>
            index.usersByKey.has(userKey),
        );

        if (realMembers.length === 0) {
            issues.push({
                code: "EMPTY_GROUP_GRANT",
                severity: "info",
                detail: `group ${group.name || "(no name)"} [${groupKey}] has no members that exist, so the grant reaches nobody`,
            });
        }

        // A grant through a group belonging to some other part of the tree
        // still works, because the rule never looks at where the group lives.
        // It is usually the wrong group picked from a long list.
        if (group.hub && !(file.ancestors || []).includes(group.hub)) {
            issues.push({
                code: "CROSS_HUB_GROUP",
                severity: "info",
                detail: `group ${group.name || "(no name)"} [${groupKey}] belongs to hub ${group.hub}, which is not above this file`,
            });
        }
    });

    let ghostManagers = (file.managers || []).filter(
        (userKey) => !index.usersByKey.has(userKey),
    );

    if (ghostManagers.length > 0) {
        issues.push({
            code: "GHOST_MANAGER",
            severity: "info",
            detail: `file.managers holds ${ghostManagers.length} keys with no user behind them`,
            userKeys: ghostManagers,
        });
    }

    if ((file.managers || []).length === 0) {
        issues.push({
            code: "NO_MANAGERS",
            severity: "warning",
            detail: "the file has no managers, so nobody can change its permissions",
        });
    }

    if (file.uploadedBy && !index.usersByKey.has(file.uploadedBy)) {
        issues.push({
            code: "GHOST_UPLOADER",
            severity: "info",
            detail: `file.uploadedBy is ${file.uploadedBy}, who no longer exists`,
        });
    }

    return issues;
}

/**
 * One file's findings for the sweep. Audience problems come first because a
 * file nobody can see is the only finding here that is certainly wrong.
 */
export function auditFile(file, index) {
    let audience = fileViewers(file, index);
    let findings = [];

    if (audience.viewers.length === 0) {
        findings.push({
            code: "NO_VIEWERS",
            severity: "critical",
            detail:
                audience.globalRoleOnly > 0
                    ? `nobody can see this file except the ${audience.globalRoleOnly} System Digital Managers`
                    : "nobody can see this file at all",
        });
    } else if (
        audience.viewers.length === 1 &&
        file.uploadedBy &&
        audience.viewers[0].user.key === file.uploadedBy &&
        audience.viewers[0].reasons.every(
            (reason) => reason.type === "uploader" || reason.type === "manager",
        )
    ) {
        findings.push({
            code: "UPLOADER_ONLY",
            severity: "warning",
            detail: "only the person who uploaded it can see this file",
        });
    }

    findings = findings
        .concat(fileReachability(file, index))
        .concat(fileGrantIssues(file, index));

    return {
        fileKey: file.key,
        fileName: file.displayName || file.filename || file.key,
        path: filePathString(file, index),
        viewers: audience.viewers.length,
        globalRoleOnly: audience.globalRoleOnly,
        findings: findings,
    };
}

/**
 * Individual grants pointing at files that are gone. Both delete paths clean
 * these up, so anything found here predates that or was left by a delete that
 * did not finish. Harmless, but it is the same list a person would have to read
 * to answer "why is this file in my user record".
 */
export function orphanIndividualGrants(users, files) {
    let fileKeys = new Set(files.map((file) => file.key));
    let orphans = [];

    users.forEach((user) => {
        (user.files || []).forEach((fileKey) => {
            if (!fileKeys.has(fileKey)) {
                orphans.push({
                    userKey: user.key,
                    userName: user.fullName,
                    fileKey: fileKey,
                });
            }
        });
    });

    return orphans;
}

// Hub managers who can open the folder and cannot open the file. Not a fault:
// the rule never consults folder.managers. It is the gap between what managing
// a hub looks like and what it grants, and it is the question this tool gets
// asked most.
export function managersWithoutAccess(file, index) {
    let folder = index.foldersByKey.get(file.folder);

    if (!folder) {
        return [];
    }

    let walked = folderChain(file.folder, index);
    let managerKeys = new Set();

    walked.chain.forEach((f) =>
        (f.managers || []).forEach((key) => managerKeys.add(key)),
    );

    let viewerKeys = new Set(
        fileViewers(file, index).viewers.map((viewer) => viewer.user.key),
    );

    return [...managerKeys]
        .filter((key) => !viewerKeys.has(key))
        .filter((key) => !index.globalRoleKeys.has(key))
        .map((key) => index.usersByKey.get(key))
        .filter((user) => user !== undefined);
}

/**
 * How the audience breaks down by grant, which the headline count cannot show:
 * a person holding two grants is one viewer and appears under both, so these
 * add up to more than the number of people.
 *
 * only counts the people for whom this is the single thing keeping their
 * access. It is the number that answers "who would lose the file if we took
 * this grant away", and the reason the breakdown is worth printing at all.
 *
 * The global role is left out. It is not a grant on the file, it is reported
 * separately, and counting it here would make a manager who happens to hold the
 * role look like they had two grants.
 */
export function summariseGrants(viewers) {
    let byType = {
        uploader: { people: 0, only: 0 },
        manager: { people: 0, only: 0 },
        individual: { people: 0, only: 0 },
    };
    let groups = new Map();
    let groupTotal = { people: 0, only: 0 };

    viewers.forEach((viewer) => {
        let grants = viewer.reasons.filter((reason) => reason.type !== "role");
        let solo = grants.length === 1;

        grants.forEach((reason) => {
            if (reason.type === "group") {
                let group = groups.get(reason.groupKey);

                if (!group) {
                    group = {
                        groupKey: reason.groupKey,
                        groupName: reason.groupName,
                        people: 0,
                        only: 0,
                    };
                    groups.set(reason.groupKey, group);
                }

                group.people += 1;

                if (solo) {
                    group.only += 1;
                }

                return;
            }

            if (byType[reason.type]) {
                byType[reason.type].people += 1;

                if (solo) {
                    byType[reason.type].only += 1;
                }
            }
        });

        // Counted once however many of the file's groups the person is in
        if (grants.some((reason) => reason.type === "group")) {
            groupTotal.people += 1;

            if (grants.every((reason) => reason.type === "group")) {
                groupTotal.only += 1;
            }
        }
    });

    return {
        byType: byType,
        groups: [...groups.values()].sort((a, b) => b.people - a.people),
        groupTotal: groupTotal,
    };
}

// Totals for the summary above the log
export function summariseFindings(rows) {
    let counts = new Map();

    rows.forEach((row) =>
        row.findings.forEach((finding) =>
            counts.set(finding.code, (counts.get(finding.code) || 0) + 1),
        ),
    );

    return {
        filesWithFindings: rows.filter((row) => row.findings.length > 0).length,
        byCode: counts,
        critical: rows.filter((row) =>
            row.findings.some((finding) => finding.severity === "critical"),
        ).length,
    };
}
